import { memo, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import BigNumber from 'bignumber.js'
import clsx from 'clsx'
import { userConfigState } from '@/recoil/UserConfig'
import { latestNotificationValue } from '@/recoil/Notification'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import { ORDER_SIDE } from '@/constants/order'
import {
  COLOR_WHITE,
  CHART_POSITIVE_COLOR,
  CHART_NEGATIVE_COLOR,
  CHART_POSITIVE_DARKEN_COLOR,
  CHART_NEGATIVE_DARKEN_COLOR,
  CHART_COLOR_OVERRIDES
} from '@/constants/color'
import fetcher from '@/api/fetcher'
import debounce from '@/utils/debounce'

function convertChartTimestamp(t) {
  // 10 digits -> 13 digits
  return BigNumber(t).times(1000).toNumber()
}

function getSymbol(widget) {
  return widget.activeChart().symbol().replace('ORDERLY:', '')
}

function toggleSubscribeSymbolChange(widget, callback, subscribe = true) {
  const action = subscribe ? 'subscribe' : 'unsubscribe'
  widget._ready &&
    widget.activeChart().onSymbolChanged()[action](null, callback)
}

function drawOrderLine(widget, orderList, orderLineMap) {
  orderList.forEach(({ order_id, symbol, price, quantity, executed, side }) => {
    if (!orderLineMap[order_id]) {
      const isBuyOrder = side === ORDER_SIDE.buy
      const textColor = COLOR_WHITE
      const labelColor = isBuyOrder
        ? CHART_POSITIVE_COLOR
        : CHART_NEGATIVE_COLOR
      const labelContentColor = isBuyOrder
        ? CHART_POSITIVE_DARKEN_COLOR
        : CHART_NEGATIVE_DARKEN_COLOR
      orderLineMap[order_id] = widget
        .activeChart()
        .createOrderLine()
        .setBodyTextColor(textColor)
        .setBodyBackgroundColor(labelColor)
        .setBodyBorderColor(labelContentColor)
        .setQuantityTextColor(textColor)
        .setQuantityBackgroundColor(labelContentColor)
        .setQuantityBorderColor(labelContentColor)
        .setCancelButtonIconColor(textColor)
        .setCancelButtonBackgroundColor(labelColor)
        .setCancelButtonBorderColor(labelContentColor)
        .setLineColor(labelColor)
        .setLineStyle(2)
        .onCancel(() => {
          orderLineMap[order_id].remove()
          delete orderLineMap[order_id]
          fetcher.cancelOrder(symbol, order_id)
        })
    }
    orderLineMap[order_id]
      .setText(side)
      .setPrice(price)
      .setQuantity(BigNumber(quantity).minus(executed).toNumber())
  })
}

function drawExecutionShape(widget, orderHistory, orderExecutionShapeMap) {
  orderHistory.forEach(
    ({ order_id, side, average_executed_price, updated_time }) => {
      if (!orderExecutionShapeMap[order_id]) {
        const arrowColor =
          side === ORDER_SIDE.buy ? CHART_POSITIVE_COLOR : CHART_NEGATIVE_COLOR
        orderExecutionShapeMap[order_id] = widget
          .activeChart()
          .createExecutionShape()
          .setDirection(side.toLowerCase())
          .setArrowColor(arrowColor)
          .setTime(
            BigNumber(updated_time)
              .div(1000)
              .dp(0, BigNumber.ROUND_DOWN)
              .toNumber()
          )
          .setPrice(average_executed_price)
      }
    }
  )
}

function removeTradingPrimitive(map) {
  Object.keys(map).forEach(key => {
    map[key].remove()
    delete map[key]
  })
}

export default memo(function TradingView({
  symbol = 'AAPL',
  datafeedUrl = 'https://demo_feed.tradingview.com',
  openOrderList = [],
  onSymbolUpdate = () => {}
}) {
  const { walletReady } = useWalletSelector()
  const tvWidget = useRef(null)
  const container = useRef(null)
  const orderLineMap = useRef({})
  const executionShapeMap = useRef({})
  const initialSymbol = useRef(symbol)

  const [userConfig] = useRecoilState(userConfigState)
  const latestNotification = useRecoilValue(latestNotificationValue)

  const [isReady, setIsReady] = useState(false)
  const [chartDataReady, setChartDataReady] = useState(true)

  const debounceGetOrderHistory = useMemo(
    () =>
      debounce((marketSymbol, { from, to }) => {
        if (to <= 0) {
          return
        }
        fetcher
          .getOrders({
            symbol: marketSymbol,
            start_t: convertChartTimestamp(from),
            end_t: convertChartTimestamp(to),
            status: 'FILLED',
            size: 500
          })
          .then(
            ({ rows }) =>
              tvWidget.current._ready &&
              drawExecutionShape(
                tvWidget.current,
                rows,
                executionShapeMap.current
              )
          )
      }, 300),
    []
  )

  const symbolUpdateCallback = useCallback(
    ({ ticker }) => {
      if (ticker) {
        onSymbolUpdate(ticker)
        removeTradingPrimitive(executionShapeMap.current)
      }
      tvWidget.current._ready = true
      setChartDataReady(true)
    },
    [onSymbolUpdate]
  )

  const updateOrderLine = useCallback(() => {
    drawOrderLine(tvWidget.current, openOrderList, orderLineMap.current)
  }, [openOrderList])

  useEffect(() => {
    if (tvWidget.current) {
      return
    }
    const widgetOptions = {
      symbol: initialSymbol.current,
      datafeed: new window.Datafeeds.UDFCompatibleDatafeed(datafeedUrl),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      interval: 15,
      container: container.current,
      library_path: '/static/js/charting_library/',
      custom_css_url: '/static/css/trading-view.css?20230117',
      locale: 'en',
      disabled_features: [
        'use_localstorage_for_settings',
        'header_symbol_search'
      ],
      enabled_features: [
        'hide_left_toolbar_by_default',
        'side_toolbar_in_fullscreen_mode'
      ],
      overrides: {
        ...CHART_COLOR_OVERRIDES,
        'timeScale.rightOffset': 2,
        'paneProperties.topMargin': 20
      },
      fullscreen: false,
      autosize: true,
      theme: 'Dark'
    }

    tvWidget.current = new window.TradingView.widget(widgetOptions)

    tvWidget.current.onChartReady(() => {
      setIsReady(true)
      tvWidget.current.headerReady().then(() => {
        const button = tvWidget.current.createButton({ align: 'right' })
        button.classList.toggle('brand-logo-container')
        button.innerHTML = `
          <img class="brand-logo" src="/static/img/btse-mascot.svg" alt="mascot logo" />
          BTSE DEX
        `
      })
    })
    return () => {
      if (tvWidget.current) {
        tvWidget.current._ready = false
        tvWidget.current.remove()
        tvWidget.current = null
      }
    }
  }, [datafeedUrl])

  useEffect(() => {
    const widget = tvWidget.current
    if (!isReady || !widget) {
      return
    }

    toggleSubscribeSymbolChange(widget, symbolUpdateCallback)
    return () =>
      toggleSubscribeSymbolChange(widget, symbolUpdateCallback, false)
  }, [isReady, symbolUpdateCallback])

  useEffect(() => {
    const widget = tvWidget.current
    if (!isReady || !widget || !walletReady) {
      return
    }

    const removeExecutionShape = () =>
      removeTradingPrimitive(executionShapeMap.current)
    const createExecutionShape = d =>
      debounceGetOrderHistory(getSymbol(widget), d)

    if (userConfig.ordersOnChart && widget._ready) {
      const chart = widget.activeChart()
      chart.onIntervalChanged().subscribe(null, removeExecutionShape)
      chart.onVisibleRangeChanged().subscribe(null, createExecutionShape)
      createExecutionShape(widget.activeChart().getVisibleRange())
    }
    return () => {
      if (widget._ready) {
        const chart = widget.activeChart()
        chart.onIntervalChanged().unsubscribe(null, removeExecutionShape)
        chart.onVisibleRangeChanged().unsubscribe(null, createExecutionShape)
      }
    }
  }, [userConfig.ordersOnChart, isReady, walletReady, debounceGetOrderHistory])

  useEffect(() => {
    const widget = tvWidget.current
    if (widget && widget._ready && symbol !== getSymbol(widget)) {
      widget.activeChart().setSymbol(symbol)
      widget._ready = false
      setChartDataReady(false)
    }
  }, [isReady, symbol])

  useEffect(() => {
    const widget = tvWidget?.current
    if (!userConfig.ordersOnChart) {
      removeTradingPrimitive(orderLineMap.current)
      removeTradingPrimitive(executionShapeMap.current)
      return
    }

    if (!isReady || !chartDataReady || !widget?._ready || !walletReady) {
      return
    }

    if (!widget.activeChart().dataReady()) {
      widget.activeChart().dataReady(() => updateOrderLine())
      return
    }

    updateOrderLine()
    debounceGetOrderHistory(
      getSymbol(widget),
      widget.activeChart().getVisibleRange()
    )
  }, [
    userConfig.ordersOnChart,
    isReady,
    chartDataReady,
    walletReady,
    updateOrderLine,
    debounceGetOrderHistory
  ])

  useEffect(() => {
    const widget = tvWidget?.current
    const {
      orderId,
      side,
      status,
      executedPrice,
      timestamp,
      executedTimestamp
    } = latestNotification || {}
    if (
      !userConfig.ordersOnChart ||
      !isReady ||
      !widget?._ready ||
      status !== 'FILLED'
    ) {
      return
    }
    drawExecutionShape(
      widget,
      [
        {
          order_id: orderId,
          side,
          average_executed_price: executedPrice,
          updated_time: timestamp || executedTimestamp
        }
      ],
      executionShapeMap.current
    )
  }, [userConfig.ordersOnChart, isReady, latestNotification])

  return (
    <div
      ref={container}
      className={clsx('chart-container', {
        'loading-overlay': !isReady
      })}
    />
  )
})
